import { PocDivider } from 'commons/components/components-wrapper/poc-divider.component';
import { PocListItem } from 'commons/components/components-wrapper/poc-list-item.component';
import { PocTypography } from 'commons/components/components-wrapper/poc-typography.component';

import { TooltipedElementComponent } from 'commons/components/tooltiped-element';
import type { PocInputValidity } from 'commons/types';
import { debounce } from 'commons/utils/functions.utils';
import { useTranslation } from 'core/i18n/i18n';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { QcProperties } from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-device-configuration-status-tracking/settings-device-configuration-status-tracking.model';
import { DeviceConfigStatusTrackingCard } from '../device-config-status-tracking-card/device-config-status-tracking-card.component';
import {
    DeviceConfigStatusTrackingAssistiveText,
    DeviceConfigStatusTrackingInput,
    DeviceConfigStatusTrackingRightCol,
    DeviceConfigStatusTrackingRow,
    DeviceConfigStatusTrackingRowTypography,
} from '../status-tracking-row.style';
import { STATUS_TRACKING_CHANGE_DEBOUNCE_MS, STATUS_TRACKING_TRANSLATION_PREFIX } from '../../constants';
import { QC_HOURS, QC_LEVELS, QC_LOCKOUT_PERIODS } from './qc-status-tracking-card.constants';
import { DeviceConfigStatusChipInfo, QcSelectDropdownStyled } from './qc-status-tracking-card.style';
import { useQcStatusTrackingCardValidation } from './use-qc-status-tracking-card-validation.hook';

interface QcStatusTrackingCardProps {
    values: QcProperties;
    onValuesChange?: (values: QcProperties) => void;
    onValidityChange?: (isValid: PocInputValidity) => void;
}

export const QcStatusTrackingCard = ({ values, onValuesChange, onValidityChange }: QcStatusTrackingCardProps) => {
    const [qcStatus, setQcStatus] = useState<QcProperties>(values);
    const { t } = useTranslation();

    const [previousValues, setPreviousValues] = useState(values);
    if (values !== previousValues) {
        setPreviousValues(values);
        setQcStatus(values);
    }

    const { validity, getValidationMessageByType, checkValidation } = useQcStatusTrackingCardValidation({
        onValidityChange,
    });

    const onChange = useCallback(
        (field: keyof QcProperties, value: number | string | boolean) => {
            const updatedStatus = {
                ...qcStatus,
                [field]: value,
            } as QcProperties;

            setQcStatus(updatedStatus);

            const isValid = checkValidation(updatedStatus);

            if (isValid.state === 'valid') {
                onValuesChange?.(updatedStatus);
            }
        },
        [checkValidation, onValuesChange]
    );

    const handleChange = useMemo(() => debounce(onChange, STATUS_TRACKING_CHANGE_DEBOUNCE_MS), [onChange]);

    useEffect(
        () => () => {
            handleChange.cancel();
        },
        [handleChange]
    );

    return (
        <DeviceConfigStatusTrackingCard
            title={t(`${STATUS_TRACKING_TRANSLATION_PREFIX}.qc.title`)}
            description={t(`${STATUS_TRACKING_TRANSLATION_PREFIX}.qc.description`)}
            toggleChecked={qcStatus?.enabled}
            onToggleCheckedChange={(value) => handleChange('enabled', value)}
        >
            <DeviceConfigStatusTrackingRow>
                <DeviceConfigStatusTrackingRowTypography variant="subtitle1">
                    {t(`${STATUS_TRACKING_TRANSLATION_PREFIX}.qc.numQcLevels`)}
                </DeviceConfigStatusTrackingRowTypography>
                <QcSelectDropdownStyled
                    placeholder={t(`${STATUS_TRACKING_TRANSLATION_PREFIX}.qc.placeholder.choose`)}
                    data-testid="qc-alert-levels-input"
                    value={`${qcStatus?.levels ?? ''}`}
                    variant="outlined"
                    size="small"
                    noClearIcon
                    onValueChange={(e) => e.detail && handleChange('levels', +e.detail)}
                    disabled={!qcStatus?.enabled}
                >
                    {QC_LEVELS.map((level) => (
                        <PocListItem key={level} value={level}>
                            {level}
                        </PocListItem>
                    ))}
                </QcSelectDropdownStyled>
            </DeviceConfigStatusTrackingRow>
            <PocDivider />
            <DeviceConfigStatusTrackingRow>
                <DeviceConfigStatusTrackingRowTypography>
                    {t(`${STATUS_TRACKING_TRANSLATION_PREFIX}.qc.qcLockout`)}
                </DeviceConfigStatusTrackingRowTypography>
                <QcSelectDropdownStyled
                    placeholder={t(`${STATUS_TRACKING_TRANSLATION_PREFIX}.qc.placeholder.period`)}
                    data-testid="qc-alert-type-input"
                    value={`${qcStatus?.qcType ?? ''}`}
                    variant="outlined"
                    size="small"
                    noClearIcon
                    onValueChange={(e) => e.detail && handleChange('qcType', e.detail[0])}
                    disabled={!qcStatus?.enabled}
                >
                    {QC_LOCKOUT_PERIODS.map((periods) => (
                        <PocListItem key={`qc-lockout-${periods}`} value={periods}>
                            {t(`${STATUS_TRACKING_TRANSLATION_PREFIX}.qc.period.${periods}`)}
                        </PocListItem>
                    ))}
                </QcSelectDropdownStyled>
            </DeviceConfigStatusTrackingRow>
            <DeviceConfigStatusTrackingRow className="flex-end">
                <DeviceConfigStatusTrackingRightCol>
                    <PocTypography variant="subtitle1">Days</PocTypography>
                    <DeviceConfigStatusTrackingInput
                        data-testid="qc-alert-days-input"
                        value={`${qcStatus?.days ?? ''}`}
                        variant="outlined"
                        size="small"
                        type="number"
                        min={0}
                        max={365}
                        noClearIcon
                        disabled={!qcStatus?.enabled}
                        validity={validity}
                        onValueChange={(e) => e.detail && handleChange('days', +e.detail)}
                    >
                        <DeviceConfigStatusTrackingAssistiveText id="warning-days-tooltip">
                            <TooltipedElementComponent
                                anchorId="warning-days-tooltip"
                                hideTooltip={!validity || validity.state === 'valid'}
                                tooltipText={getValidationMessageByType()}
                            />
                            {getValidationMessageByType()}
                        </DeviceConfigStatusTrackingAssistiveText>
                    </DeviceConfigStatusTrackingInput>
                </DeviceConfigStatusTrackingRightCol>
                <DeviceConfigStatusTrackingRightCol>
                    <PocTypography variant="subtitle1">
                        {t(`${STATUS_TRACKING_TRANSLATION_PREFIX}.prefix.hours`)}
                    </PocTypography>
                    <QcSelectDropdownStyled
                        placeholder={t(`${STATUS_TRACKING_TRANSLATION_PREFIX}.qc.placeholder.choose`)}
                        data-testid="qc-alert-hours-input"
                        value={`${qcStatus?.hours ?? ''}`}
                        variant="outlined"
                        size="small"
                        noClearIcon
                        disabled={!qcStatus?.enabled}
                        onValueChange={(e) => e.detail && handleChange('hours', +e.detail)}
                    >
                        {QC_HOURS.map((hours) => (
                            <PocListItem key={`qc-hour-${hours}`} value={hours}>
                                {hours}
                            </PocListItem>
                        ))}
                    </QcSelectDropdownStyled>
                </DeviceConfigStatusTrackingRightCol>
            </DeviceConfigStatusTrackingRow>
            <DeviceConfigStatusChipInfo>
                <PocTypography variant="subtitle2">
                    {t(`${STATUS_TRACKING_TRANSLATION_PREFIX}.qc.fallback.title`)}
                </PocTypography>
                <PocTypography variant="subtitle2" style={{ fontWeight: 'normal' }}>
                    {t(`${STATUS_TRACKING_TRANSLATION_PREFIX}.qc.fallback.description`)}
                </PocTypography>
            </DeviceConfigStatusChipInfo>
        </DeviceConfigStatusTrackingCard>
    );
};
